package features.payment

import io.ktor.client.request.header
import io.ktor.client.request.post
import io.ktor.client.request.setBody
import io.ktor.client.statement.bodyAsText
import io.ktor.http.contentType
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.put
import kotlinx.serialization.json.putJsonArray
import kotlinx.serialization.json.putJsonObject
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive
import kotlinx.serialization.json.booleanOrNull

private val jsonParser = Json { ignoreUnknownKeys = true; isLenient = true }

interface PaymentRepository {
    suspend fun crearSesionSPEI(
        monto: Int, ruta: String, fecha: String, horario: String,
        asientos: String, email: String,
        pasajeros: String, asientosArray: List<Int>,
        origenDestino: String, totalPagado: Int,
        puntoSubida: String = "", urlSalida: String = "",
        esIdaYVuelta: Boolean = false, fechaVuelta: String = "",
        horarioVuelta: String = "", rutaVuelta: String = "",
        asientosVueltaArray: List<Int> = emptyList(), pasajerosVuelta: String = "",
        puntoSubidaVuelta: String = "",
        requiereFactura: Boolean = false, rfc: String = "",
        razonSocial: String = "", regimenFiscal: String = "",
        usoCfdi: String = "", cpFiscal: String = ""
    ): PaymentRepositoryImpl.SpeiResult?

    suspend fun crearSesionOxxo(
        monto: Int, ruta: String, fecha: String, horario: String,
        asientos: String, email: String,
        pasajeros: String, asientosArray: List<Int>,
        origenDestino: String, totalPagado: Int,
        puntoSubida: String = "", urlSalida: String = "",
        esIdaYVuelta: Boolean = false, fechaVuelta: String = "",
        horarioVuelta: String = "", rutaVuelta: String = "",
        asientosVueltaArray: List<Int> = emptyList(), pasajerosVuelta: String = "",
        puntoSubidaVuelta: String = "",
        requiereFactura: Boolean = false, rfc: String = "",
        razonSocial: String = "", regimenFiscal: String = "",
        usoCfdi: String = "", cpFiscal: String = ""
    ): PaymentRepositoryImpl.OxxoResult?

    suspend fun crearSesionTarjeta(
        monto: Int, ruta: String, fecha: String, horario: String,
        asientos: String, email: String,
        pasajeros: String, asientosArray: List<Int>,
        origenDestino: String, totalPagado: Int,
        puntoSubida: String = "", urlSalida: String = "",
        esIdaYVuelta: Boolean = false, fechaVuelta: String = "",
        horarioVuelta: String = "", rutaVuelta: String = "",
        asientosVueltaArray: List<Int> = emptyList(), pasajerosVuelta: String = "",
        puntoSubidaVuelta: String = "",
        requiereFactura: Boolean = false, rfc: String = "",
        razonSocial: String = "", regimenFiscal: String = "",
        usoCfdi: String = "", cpFiscal: String = ""
    ): PaymentRepositoryImpl.CardResult?
}

class PaymentRepositoryImpl : PaymentRepository {
    data class SpeiResult(
        val clabe: String,
        val banco: String,
        val referencia: String,
        val monto: Int,
        val paymentIntentId: String
    )

    data class OxxoResult(
        val oxxoNumber: String,
        val voucherUrl: String,
        val referencia: String,
        val monto: Int,
        val paymentIntentId: String
    )

    /** URL hosted del Stripe Checkout de tarjeta; el frontend la abre en el navegador. */
    data class CardResult(
        val checkoutUrl: String,
        val sessionId: String,
        val paymentIntentId: String,
        val monto: Int
    )

    private fun buildPaymentBody(
        monto: Int, ruta: String, fecha: String, horario: String,
        asientos: String, email: String, pasajeros: String,
        asientosArray: List<Int>, origenDestino: String, totalPagado: Int,
        puntoSubida: String = "", urlSalida: String = "",
        esIdaYVuelta: Boolean = false, fechaVuelta: String = "",
        horarioVuelta: String = "", rutaVuelta: String = "",
        asientosVueltaArray: List<Int> = emptyList(), pasajerosVuelta: String = "",
        puntoSubidaVuelta: String = "",
        requiereFactura: Boolean = false, rfc: String = "",
        razonSocial: String = "", regimenFiscal: String = "",
        usoCfdi: String = "", cpFiscal: String = ""
    ): String {
        val body = buildJsonObject {
            putJsonObject("data") {
                put("appVersion", network.CloudFunctions.appVersionCode)
                // Sandbox de Stripe. El servidor solo lo respeta para master/desarrollador;
                // para cualquier otro rol cobra en live aunque llegue en true.
                if (network.ModoPruebaPagos.activo.value) put("modoPrueba", true)
                put("monto", monto)
                put("ruta", ruta)
                put("fecha", fecha)
                put("horario", horario)
                put("asientos", asientos)
                put("email", email)
                put("pasajeros", pasajeros)
                putJsonArray("asientosArray") {
                    asientosArray.forEach { add(JsonPrimitive(it)) }
                }
                put("origenDestino", origenDestino)
                put("totalPagado", totalPagado)
                if (puntoSubida.isNotBlank()) put("puntoSubida", puntoSubida)
                if (urlSalida.isNotBlank()) put("urlSalida", urlSalida)
                if (esIdaYVuelta) {
                    put("esIdaYVuelta", true)
                    put("fechaVuelta", fechaVuelta)
                    put("horarioVuelta", horarioVuelta)
                    put("rutaVuelta", rutaVuelta)
                    putJsonArray("asientosVueltaArray") {
                        asientosVueltaArray.forEach { add(JsonPrimitive(it)) }
                    }
                    if (pasajerosVuelta.isNotBlank()) put("pasajerosVuelta", pasajerosVuelta)
                    if (puntoSubidaVuelta.isNotBlank()) put("puntoSubidaVuelta", puntoSubidaVuelta)
                }
                if (requiereFactura) {
                    put("requiereFactura", true)
                    put("rfc", rfc)
                    put("razonSocial", razonSocial)
                    put("regimenFiscal", regimenFiscal)
                    put("usoCfdi", usoCfdi)
                    put("cpFiscal", cpFiscal)
                }
            }
        }
        return body.toString()
    }

    private fun extractString(obj: JsonObject, key: String): String {
        return obj[key]?.jsonPrimitive?.content ?: ""
    }

    private fun extractBoolean(obj: JsonObject, key: String): Boolean {
        return obj[key]?.jsonPrimitive?.booleanOrNull ?: false
    }

    private suspend fun getAuthToken(): String {
        return try {
            network.FirebaseClient.auth.currentUser?.getIdToken(true) ?: ""
        } catch (e: Exception) { "" }
    }

    override suspend fun crearSesionSPEI(
        monto: Int, ruta: String, fecha: String, horario: String,
        asientos: String, email: String,
        pasajeros: String, asientosArray: List<Int>,
        origenDestino: String, totalPagado: Int,
        puntoSubida: String, urlSalida: String,
        esIdaYVuelta: Boolean, fechaVuelta: String,
        horarioVuelta: String, rutaVuelta: String,
        asientosVueltaArray: List<Int>, pasajerosVuelta: String,
        puntoSubidaVuelta: String,
        requiereFactura: Boolean, rfc: String,
        razonSocial: String, regimenFiscal: String,
        usoCfdi: String, cpFiscal: String
    ): SpeiResult? {
        return try {
            val client = network.sharedHttpClient
            val urlString = network.CloudFunctions.CREAR_SESION_SPEI
            val bodyString = buildPaymentBody(monto, ruta, fecha, horario, asientos, email, pasajeros, asientosArray, origenDestino, totalPagado, puntoSubida, urlSalida, esIdaYVuelta, fechaVuelta, horarioVuelta, rutaVuelta, asientosVueltaArray, pasajerosVuelta, puntoSubidaVuelta, requiereFactura, rfc, razonSocial, regimenFiscal, usoCfdi, cpFiscal)
            val token = getAuthToken()

            val response = client.post(urlString) {
                contentType(io.ktor.http.ContentType.Application.Json)
                header("Authorization", "Bearer $token")
                setBody(bodyString)
            }

            val responseText = response.bodyAsText()

            val responseJson = jsonParser.parseToJsonElement(responseText).jsonObject
            val result = responseJson["result"]?.jsonObject ?: responseJson

            if (extractBoolean(result, "success")) {
                // Usar monto del server (recalculado) si está disponible, sino fallback al local
                val montoServer = result["monto"]?.jsonPrimitive?.content?.toIntOrNull() ?: monto
                SpeiResult(
                    clabe = extractString(result, "clabe"),
                    banco = extractString(result, "banco"),
                    referencia = extractString(result, "referencia"),
                    monto = montoServer,
                    paymentIntentId = extractString(result, "paymentIntentId")
                )
            } else {
                println("[SmartVan] SPEI error: respuesta sin success. Response: ${responseText.take(200)}")
                null
            }
        } catch (e: Exception) {
            println("[SmartVan] SPEI excepción: ${e.message}")
            null
        }
    }

    override suspend fun crearSesionOxxo(
        monto: Int, ruta: String, fecha: String, horario: String,
        asientos: String, email: String,
        pasajeros: String, asientosArray: List<Int>,
        origenDestino: String, totalPagado: Int,
        puntoSubida: String, urlSalida: String,
        esIdaYVuelta: Boolean, fechaVuelta: String,
        horarioVuelta: String, rutaVuelta: String,
        asientosVueltaArray: List<Int>, pasajerosVuelta: String,
        puntoSubidaVuelta: String,
        requiereFactura: Boolean, rfc: String,
        razonSocial: String, regimenFiscal: String,
        usoCfdi: String, cpFiscal: String
    ): OxxoResult? {
        return try {
            val client = network.sharedHttpClient
            val urlString = network.CloudFunctions.CREAR_SESION_OXXO
            val bodyString = buildPaymentBody(monto, ruta, fecha, horario, asientos, email, pasajeros, asientosArray, origenDestino, totalPagado, puntoSubida, urlSalida, esIdaYVuelta, fechaVuelta, horarioVuelta, rutaVuelta, asientosVueltaArray, pasajerosVuelta, puntoSubidaVuelta, requiereFactura, rfc, razonSocial, regimenFiscal, usoCfdi, cpFiscal)
            val token = getAuthToken()

            val response = client.post(urlString) {
                contentType(io.ktor.http.ContentType.Application.Json)
                header("Authorization", "Bearer $token")
                setBody(bodyString)
            }

            val responseText = response.bodyAsText()

            val responseJson = jsonParser.parseToJsonElement(responseText).jsonObject
            val result = responseJson["result"]?.jsonObject ?: responseJson

            if (extractBoolean(result, "success")) {
                val montoServer = result["monto"]?.jsonPrimitive?.content?.toIntOrNull() ?: monto
                OxxoResult(
                    oxxoNumber = extractString(result, "oxxoNumber"),
                    voucherUrl = extractString(result, "voucherUrl"),
                    referencia = extractString(result, "referencia"),
                    monto = montoServer,
                    paymentIntentId = extractString(result, "paymentIntentId")
                )
            } else {
                println("[SmartVan] OXXO error: respuesta sin success. Response: ${responseText.take(200)}")
                null
            }
        } catch (e: Exception) {
            println("[SmartVan] OXXO excepción: ${e.message}")
            null
        }
    }

    override suspend fun crearSesionTarjeta(
        monto: Int, ruta: String, fecha: String, horario: String,
        asientos: String, email: String,
        pasajeros: String, asientosArray: List<Int>,
        origenDestino: String, totalPagado: Int,
        puntoSubida: String, urlSalida: String,
        esIdaYVuelta: Boolean, fechaVuelta: String,
        horarioVuelta: String, rutaVuelta: String,
        asientosVueltaArray: List<Int>, pasajerosVuelta: String,
        puntoSubidaVuelta: String,
        requiereFactura: Boolean, rfc: String,
        razonSocial: String, regimenFiscal: String,
        usoCfdi: String, cpFiscal: String
    ): CardResult? {
        return try {
            val client = network.sharedHttpClient
            val urlString = network.CloudFunctions.CREAR_SESION_TARJETA
            val bodyString = buildPaymentBody(monto, ruta, fecha, horario, asientos, email, pasajeros, asientosArray, origenDestino, totalPagado, puntoSubida, urlSalida, esIdaYVuelta, fechaVuelta, horarioVuelta, rutaVuelta, asientosVueltaArray, pasajerosVuelta, puntoSubidaVuelta, requiereFactura, rfc, razonSocial, regimenFiscal, usoCfdi, cpFiscal)
            val token = getAuthToken()

            val response = client.post(urlString) {
                contentType(io.ktor.http.ContentType.Application.Json)
                header("Authorization", "Bearer $token")
                setBody(bodyString)
            }

            val responseText = response.bodyAsText()
            val responseJson = jsonParser.parseToJsonElement(responseText).jsonObject
            val result = responseJson["result"]?.jsonObject ?: responseJson

            if (extractBoolean(result, "success")) {
                val montoServer = result["monto"]?.jsonPrimitive?.content?.toIntOrNull() ?: monto
                CardResult(
                    checkoutUrl = extractString(result, "url"),
                    sessionId = extractString(result, "sessionId"),
                    paymentIntentId = extractString(result, "paymentIntentId"),
                    monto = montoServer
                )
            } else {
                println("[SmartVan] TARJETA error: ${responseText.take(200)}")
                null
            }
        } catch (e: Exception) {
            println("[SmartVan] TARJETA excepción: ${e.message}")
            null
        }
    }
}
